import { useOpenS3File } from '@/components/hooks/useOpenS3File';
import { FormattedText } from '@/components/shared/components/FormattedText';
import { Linkify } from '@/components/shared/components/Linkify';
import { useFeatureFlags } from '@/components/shared/constants/featureFlags';

import {
    AutoResolveButton,
    Avatar,
    AvatarImage,
    ContinuationTime,
    NoNotesText,
    NoteBody,
    NoteEntry,
    NoteHeader,
    NoteText,
    ResolvedBadge,
    ResolvedNoteText,
    TimelineWrapper,
} from './ApplicantConditions.StyledComponents';
import { ConditionFile, ConditionNote } from './ApplicantConditions.types';
import { formatDateTime, formatTime, getAvatar, getFileTypeLabel } from './ApplicantConditions.utils';

interface Props {
    notes: ConditionNote[];
    files?: ConditionFile[];
    /** When provided, each group of system-generated notes renders one Auto Resolve button that invokes this. */
    onAutoResolve?: () => void;
    highlightNoteId?: string | null;
}

type TimelineItem =
    | { type: 'note'; date: string; data: ConditionNote }
    | { type: 'file'; date: string; data: ConditionFile };

const isSameDay = (a: string, b: string) => new Date(a).toDateString() === new Date(b).toDateString();

/**
 * Slack-style grouping: a note joins the entry above it (no avatar or header of its own) when both
 * are ordinary notes by the same author on the same day. A file, a resolution note, a different
 * author, or a new day starts a fresh group.
 */
const continuesGroup = (prev: TimelineItem | undefined, item: TimelineItem): boolean =>
    prev?.type === 'note' &&
    item.type === 'note' &&
    !prev.data.isResolutionNote &&
    !item.data.isResolutionNote &&
    prev.data.createdBy === item.data.createdBy &&
    isSameDay(prev.date, item.date);

const AvatarLabel = ({ name }: { name: string }) => {
    const avatar = getAvatar(name);
    return (
        <Avatar>
            {avatar.type === 'image' ? (
                <AvatarImage src={avatar.src} alt={avatar.alt} />
            ) : (
                avatar.initials
            )}
        </Avatar>
    );
};

export const ConditionNotesTimeline = ({
    notes,
    files = [],
    onAutoResolve,
    highlightNoteId = null,
}: Props) => {
    const { handleFileClick, getFileDragProps } = useOpenS3File();
    const { isConditionTextFormattingEnabled } = useFeatureFlags();

    const items: TimelineItem[] = [
        ...notes.map((n) => ({ type: 'note' as const, date: n.dateCreated, data: n })),
        ...files.map((f) => ({ type: 'file' as const, date: f.uploadDate, data: f })),
    ].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    if (items.length === 0) {
        return <NoNotesText>No notes yet on this condition.</NoNotesText>;
    }

    return (
        <TimelineWrapper>
            {items.map((item, index) => {
                if (item.type === 'note') {
                    const note = item.data;
                    const Body = note.isResolutionNote ? ResolvedNoteText : NoteText;
                    const isContinuation = continuesGroup(items[index - 1], item);
                    // Auto Resolve acts on the whole condition, so a group gets one button, after its last note.
                    const next = items[index + 1];
                    const endsGroup = !next || !continuesGroup(item, next);
                    return (
                        <NoteEntry
                            key={`note-${note.id}`}
                            data-condition-note-id={note.id}
                            isFocused={note.id === highlightNoteId}
                            $continuation={isContinuation}
                            $joinsNext={!endsGroup}
                        >
                            {isContinuation ? (
                                <ContinuationTime data-testid={`condition-note-time-${note.id}`}>
                                    {formatTime(note.dateCreated)}
                                </ContinuationTime>
                            ) : (
                                <AvatarLabel name={note.createdBy} />
                            )}
                            <NoteBody>
                                {isContinuation ? null : (
                                    <NoteHeader>
                                        {note.createdBy} · {formatDateTime(note.dateCreated)}
                                        {note.isResolutionNote ? (
                                            <ResolvedBadge>— Resolved</ResolvedBadge>
                                        ) : null}
                                    </NoteHeader>
                                )}
                                <Body
                                    $markdown={isConditionTextFormattingEnabled}
                                    $joinsPrev={isContinuation}
                                    $joinsNext={!endsGroup}
                                >
                                    {isConditionTextFormattingEnabled ? (
                                        <FormattedText>{note.note}</FormattedText>
                                    ) : (
                                        <Linkify>{note.note}</Linkify>
                                    )}
                                </Body>
                                {note.isSystemGenerated && onAutoResolve && endsGroup ? (
                                    <AutoResolveButton
                                        type="button"
                                        onClick={onAutoResolve}
                                        data-testid={`condition-auto-resolve-${note.id}`}
                                    >
                                        ✓ Auto Resolve
                                    </AutoResolveButton>
                                ) : null}
                            </NoteBody>
                        </NoteEntry>
                    );
                }

                const file = item.data;
                const typeLabel = getFileTypeLabel(file.type);
                return (
                    <NoteEntry key={`file-${file.id}`}>
                        <AvatarLabel name={file.uploadedBy ?? ''} />
                        <NoteBody>
                            <NoteHeader>
                                {file.uploadedBy ?? 'Unknown'} · {formatDateTime(file.uploadDate)}
                            </NoteHeader>
                            <NoteText>
                                📎{' '}
                                <a
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleFileClick(file.id);
                                    }}
                                    {...getFileDragProps(file.id, file.filename)}
                                    style={{
                                        color: '#2563eb',
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                    }}
                                >
                                    {file.filename}
                                </a>
                                {typeLabel && typeLabel !== 'Other' ? (
                                    <span style={{ color: '#6b7280', marginLeft: 8, fontSize: 12 }}>
                                        ({typeLabel})
                                    </span>
                                ) : null}
                            </NoteText>
                        </NoteBody>
                    </NoteEntry>
                );
            })}
        </TimelineWrapper>
    );
};
